/**
 * GeneralTab Component
 *
 * Composite component that orchestrates the 4 card components for the General tab:
 * - GeneralTabSummaryCard
 * - GeneralTabFinancialsCard
 * - GeneralTabProductPortfolioCard
 * - GeneralTabShareholderCard
 *
 * This component owns:
 * - Scroll state (generalFinancialsCardScrolled, generalFinancialsValuesScrollable)
 * - Ref for financials values container
 * - Auto-scroll effects for financials visibility
 * - ResizeObserver for scroll detection
 *
 * SELF-CONTAINED: All General tab scroll behavior is managed internally.
 * Parent only needs to provide data props.
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import GeneralError from '../../../errors/GeneralError';
import GeneralTabSummaryCard from './GeneralTabSummaryCard';
import GeneralTabFinancialsCard from './GeneralTabFinancialsCard';
import GeneralTabProductPortfolioCard from './GeneralTabProductPortfolioCard';
import GeneralTabShareholderCard from './GeneralTabShareholderCard';
import { isElementVisibleOnScreenPartially } from '../../../../utils/utils';

// Width of the financials' label column: narrower for figures in millions of euros.
const labelWidthOf = (layout, formatUnit) => {
  if (formatUnit === 'euroMillions') return '95px';
  return layout === 'desktop' ? '140px' : '124px';
};

/**
 * @param {Object} props
 * @param {Object} props.company - Company data object
 * @param {Object} props.financials - Financial data { years, revenues, grossProfit, etc. }
 * @param {Array} props.topManagement - Top management data
 * @param {Array} props.ultimateShareholders - Ultimate shareholders list
 * @param {boolean} [props.isLoadingShareholders] - Whether the shareholder structure is loading
 *   (defaults to isLoadingDetails)
 * @param {string|null} [props.shareholdersError] - Error of the shareholder structure request
 *   (defaults to loadDetailsError)
 * @param {boolean} props.isLoadingDetails - Whether details are loading
 * @param {boolean} props.isLoadingSupplementaryData - Whether supplementary data is loading
 * @param {string|null} props.loadDetailsError - Error message from loading details
 * @param {string|null} props.loadSupplementaryDataError - Error message from loading supplementary data
 * @param {Object|null} props.industryCode - Resolved WZ code `{ code, name, divisionName }`
 * @param {boolean} props.isIndependent - Whether company is independent (no parent)
 * @param {boolean} props.canViewExtendedData - Whether user can view extended profiler data
 * @param {string} props.layout - 'desktop' | 'mobile'
 * @param {string} props.formatUnit - Currency format unit ('euroMillions' | 'euroThousands')
 * @param {Function} props.formatCurrency - Currency formatting function
 * @param {string|null} props.companyGoogleMapsLink - Google Maps link for company address
 * @param {Function} props.onRetryLoadDetails - Callback to retry loading details
 * @param {Function} [props.onRetryShareholders] - Retries the shareholder structure request
 * @param {boolean} props.isSimilarCompanyOpen - Whether a similar company popup is open (skips scroll effects)
 */
const GeneralTab = ({
  company = {},
  registerCourt = '',
  financials = null,
  topManagement = null,
  ultimateShareholders = [],
  isLoadingShareholders,
  shareholdersError,
  isLoadingDetails = false,
  isLoadingSupplementaryData = false,
  loadDetailsError = null,
  loadSupplementaryDataError = null,
  industryCode = null,
  isIndependent = false,
  ownershipSummary = null,
  canViewDetails = true,
  canViewExtendedData = true,
  layout = 'desktop',
  formatUnit = 'euroMillions',
  formatCurrency = (v) => v,
  companyGoogleMapsLink = null,
  onRetryLoadDetails = () => {},
  onRetryShareholders = () => {},
  isSimilarCompanyOpen = false
}) => {
  // Internal scroll state - managed by this component
  const [isScrollable, setIsScrollable] = useState(false);
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const generalFinancialsValuesRef = useRef(null);

  // Calculate label style based on layout and format unit
  const labelStyle = { width: labelWidthOf(layout, formatUnit) };

  // Effect: Auto-scroll financials values on format unit change
  useEffect(() => {
    if (isSimilarCompanyOpen) {
      return;
    }

    const cardFinancialsDOM = document.querySelector(
      '.card.financials > .general-financials-layout'
    );
    if (!cardFinancialsDOM) {
      return;
    }

    setTimeout(() => {
      const financialValuesDOM = document.querySelector('.general-financials-values');
      if (financialValuesDOM?.scrollWidth) {
        financialValuesDOM.scrollTo({
          left: financialValuesDOM.scrollWidth,
          behavior: 'smooth'
        });
      }
    }, 300);
  }, [formatUnit, isSimilarCompanyOpen]);

  // Effect: Auto-scroll on initial render when financials card is visible
  useEffect(() => {
    if (isSimilarCompanyOpen) {
      return;
    }

    const cardFinancialsDOM = document.querySelector(
      '.card.financials > .general-financials-layout'
    );
    if (
      hasAutoScrolled ||
      !cardFinancialsDOM ||
      !isElementVisibleOnScreenPartially(cardFinancialsDOM, 3)
    ) {
      return;
    }

    setTimeout(() => {
      const financialValuesDOM = document.querySelector('.general-financials-values');
      if (financialValuesDOM?.scrollWidth) {
        setHasAutoScrolled(true);
        financialValuesDOM.scrollTo({
          left: financialValuesDOM.scrollWidth,
          behavior: 'smooth'
        });
      }
    }, 500);
  }, [hasAutoScrolled, isSimilarCompanyOpen, isLoadingDetails]);

  // Effect: Scroll listener for auto-scrolling when financials card becomes visible
  useEffect(() => {
    const onScroll = () => {
      if (isSimilarCompanyOpen || hasAutoScrolled) {
        return;
      }

      const cardFinancialsDOM = document.querySelector('.card.financials');
      if (cardFinancialsDOM && isElementVisibleOnScreenPartially(cardFinancialsDOM, 3)) {
        setTimeout(() => {
          const financialValuesDOM = document.querySelector('.general-financials-values');
          if (financialValuesDOM?.scrollWidth) {
            setHasAutoScrolled(true);
            financialValuesDOM.scrollTo({
              left: financialValuesDOM.scrollWidth,
              behavior: 'smooth'
            });
          }
        }, 500);
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [hasAutoScrolled, isSimilarCompanyOpen]);

  // Effect: ResizeObserver to detect scrollability + preserve the user's
  //         scroll ratio across resizes within a breakpoint. On the
  //         mobile → desktop breakpoint cross, snap to the latest year
  //         (matches the initial-load auto-scroll behavior).
  useEffect(() => {
    if (!generalFinancialsValuesRef.current) {
      setIsScrollable(false);
      return;
    }
    const el = generalFinancialsValuesRef.current;
    let scrollRatio = 1; // 1 = right edge — matches the initial auto-scroll-to-latest-year
    const mobileMq = globalThis.matchMedia('(max-width: 1200px)');
    let wasMobile = mobileMq.matches;

    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      scrollRatio = max > 0 ? el.scrollLeft / max : 1;
    };

    const resizeObserver = new ResizeObserver(([entry]) => {
      setIsScrollable(entry.target.scrollWidth > entry.target.clientWidth);
      const max = el.scrollWidth - el.clientWidth;
      if (max > 0) el.scrollLeft = scrollRatio * max;
    });

    const onMediaChange = (e) => {
      if (wasMobile && !e.matches) {
        // Mobile → desktop: snap to latest year after layout settles
        requestAnimationFrame(() => {
          scrollRatio = 1;
          el.scrollLeft = el.scrollWidth;
        });
      }
      wasMobile = e.matches;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    resizeObserver.observe(el);
    mobileMq.addEventListener('change', onMediaChange);
    return () => {
      el.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
      mobileMq.removeEventListener('change', onMediaChange);
    };
  }, [generalFinancialsValuesRef.current, isLoadingDetails]);

  // One banner for whatever failed, the details or the shareholder structure (the details
  // message covers more of the tab); Try again retries each failed request.
  const bannerError = loadDetailsError || shareholdersError || null;
  const retryFailedLoads = () => {
    if (loadDetailsError) onRetryLoadDetails();
    if (shareholdersError) onRetryShareholders();
  };

  return (
    <>
      {!!bannerError && (
        <GeneralError
          errorMessage={bannerError}
          actionMessage="You may want to:"
          actionButtontext="Try again"
          onActionButtonClick={retryFailedLoads}
          errorWrapperClassname="general-tab-error"
        />
      )}
      <div className="general-tab-cards">
        <GeneralTabSummaryCard
          company={company}
          registerCourt={registerCourt}
          isLoadingSupplementaryData={isLoadingSupplementaryData}
          loadSupplementaryDataError={loadSupplementaryDataError}
          industryCode={industryCode}
          isLoadingDetails={isLoadingDetails}
          topManagement={topManagement}
          canViewExtendedData={canViewDetails}
          companyGoogleMapsLink={companyGoogleMapsLink}
          ownershipSummary={ownershipSummary}
        />
        {/* TD-1843: Always show Financials card, even when no data available */}
        <GeneralTabFinancialsCard
          financials={financials}
          isLoading={isLoadingDetails}
          hasError={!!loadDetailsError}
          layout={layout}
          isScrollable={isScrollable}
          labelStyle={labelStyle}
          formatCurrency={formatCurrency}
          valuesRef={generalFinancialsValuesRef}
          canViewExtendedData={canViewDetails}
          showEmptyRows={canViewExtendedData}
        />
        <GeneralTabProductPortfolioCard
          productPortfolio={company.productPortfolio}
          isLoadingSupplementaryData={isLoadingSupplementaryData}
          loadSupplementaryDataError={loadSupplementaryDataError}
        />
        <GeneralTabShareholderCard
          companyId={company.id}
          companyName={company.name}
          ultimateShareholders={ultimateShareholders}
          isLoadingDetails={isLoadingShareholders ?? isLoadingDetails}
          loadDetailsError={shareholdersError === undefined ? loadDetailsError : shareholdersError}
          isIndependent={isIndependent}
          canViewExtendedData={canViewDetails}
          ownershipSummary={ownershipSummary}
          ownershipMatch={company.ownershipMatch}
        />
      </div>
    </>
  );
};

GeneralTab.propTypes = {
  company: PropTypes.object,
  /** Commercial-register court, forwarded to the Summary card (TD-2510). */
  registerCourt: PropTypes.string,
  financials: PropTypes.object,
  topManagement: PropTypes.array,
  ultimateShareholders: PropTypes.array,
  isLoadingShareholders: PropTypes.bool,
  shareholdersError: PropTypes.string,
  isLoadingDetails: PropTypes.bool,
  isLoadingSupplementaryData: PropTypes.bool,
  loadDetailsError: PropTypes.string,
  loadSupplementaryDataError: PropTypes.string,
  /** Resolved WZ code, forwarded to the Summary card */
  industryCode: PropTypes.object,
  isIndependent: PropTypes.bool,
  ownershipSummary: PropTypes.object,
  canViewDetails: PropTypes.bool,
  canViewExtendedData: PropTypes.bool,
  layout: PropTypes.oneOf(['desktop', 'mobile', 'tablet']),
  formatUnit: PropTypes.oneOf(['euroMillions', 'euroThousands']),
  formatCurrency: PropTypes.func,
  companyGoogleMapsLink: PropTypes.string,
  onRetryLoadDetails: PropTypes.func,
  onRetryShareholders: PropTypes.func,
  isSimilarCompanyOpen: PropTypes.bool
};

export default GeneralTab;
